import { useMemo, useState } from "react";
import { DndContext, PointerSensor, useSensor, useSensors, useDroppable, useDraggable, closestCenter } from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { TASK_STATUSES, TASK_STATUS_COLORS, PRIORITY_COLORS } from "./projectUtils";
import { GripVertical, User, MessageSquare, Paperclip, AlertTriangle } from "lucide-react";
import { assigneesOf, isOverdue } from "./taskUtils";
import TaskDetailDrawer from "./TaskDetailDrawer";

export default function KanbanBoard({ tasks, users, canEdit, currentUser, onChanged, project }) {
  const [detailId, setDetailId] = useState(null);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
  const nameOf = (id) => users.find(u => u.id === id)?.name || "—";

  const grouped = useMemo(() => {
    const g = Object.fromEntries(TASK_STATUSES.map(s => [s, []]));
    tasks.forEach(t => { (g[t.status] || g["To Do"]).push(t); });
    return g;
  }, [tasks]);

  const isDev = currentUser?.role === "developer";

  const onDragEnd = async (evt) => {
    const { active, over } = evt;
    if (!over) return;
    const taskId = String(active.id);
    const newStatus = String(over.id);
    if (!TASK_STATUSES.includes(newStatus)) return;
    const task = tasks.find(t => t.id === taskId);
    if (!task || task.status === newStatus) return;
    if (isDev && !assigneesOf(task).includes(currentUser.id)) {
      toast.error("You can only move tasks assigned to you");
      return;
    }
    try {
      await api.put(`/tasks/${taskId}`, { status: newStatus });
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-[11px] font-mono uppercase tracking-[0.2em] text-orange-400">Kanban</div>
          <div className="text-neutral-600 text-sm">Drag tasks to update their status</div>
        </div>
      </div>
      <DndContext sensors={sensors} onDragEnd={onDragEnd} collisionDetection={closestCenter}>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 min-h-[400px]">
          {TASK_STATUSES.map(status => (
            <Column key={status} status={status} tasks={grouped[status]} nameOf={nameOf} canEdit={canEdit} isDev={isDev} currentUserId={currentUser?.id} onOpen={setDetailId} />
          ))}
        </div>
      </DndContext>
      <TaskDetailDrawer taskId={detailId} open={!!detailId} onOpenChange={(o) => { if (!o) setDetailId(null); }} users={users} project={project} currentUser={currentUser} canManage={canEdit} onChanged={onChanged} />
    </div>
  );
}

function Column({ status, tasks, nameOf, canEdit, isDev, currentUserId, onOpen }) {
  const { setNodeRef, isOver } = useDroppable({ id: status });
  return (
    <div ref={setNodeRef} className={`panel p-3 flex flex-col gap-2 transition-colors ${isOver ? "ring-2 ring-orange-500/50" : ""}`} data-testid={`kanban-col-${status.toLowerCase().replace(/\s+/g,'-')}`}>
      <div className="flex items-center justify-between px-1">
        <span className={`text-[10px] font-mono px-2 py-0.5 rounded border ${TASK_STATUS_COLORS[status] || ""}`}>{status}</span>
        <span className="text-[10px] font-mono text-neutral-500">{tasks.length}</span>
      </div>
      <div className="space-y-2 min-h-[300px]">
        {tasks.map(t => (
          <Card key={t.id} task={t} nameOf={nameOf} onOpen={onOpen} draggable={canEdit || (isDev && assigneesOf(t).includes(currentUserId))} />
        ))}
        {tasks.length === 0 && <div className="text-[10px] font-mono text-neutral-600 text-center py-6">Empty</div>}
      </div>
    </div>
  );
}

function Card({ task, nameOf, draggable, onOpen }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id: task.id, disabled: !draggable });
  const style = { transform: CSS.Translate.toString(transform), opacity: isDragging ? 0.5 : 1 };
  const overdue = isOverdue(task);
  return (
    <div
      ref={setNodeRef}
      style={style}
      onClick={() => onOpen(task.id)}
      className={`rounded-lg border bg-[#FFFFFF] p-3 space-y-2 cursor-pointer select-none hover:border-orange-400 ${overdue ? "border-rose-300" : "border-[#E4E7EC]"} ${draggable ? "active:cursor-grabbing" : ""}`}
      data-testid={`kanban-card-${task.id}`}
      {...listeners}
      {...attributes}>
      <div className="flex items-start gap-2">
        {draggable && <GripVertical className="w-3 h-3 text-neutral-600 mt-0.5 shrink-0" />}
        <div className="flex-1 min-w-0">
          <div className="text-[9px] font-mono text-neutral-500">{task.task_id}</div>
          <div className="text-sm font-medium truncate">{task.name}</div>
        </div>
      </div>
      <div className="flex items-center justify-between gap-2">
        <span className={`text-[9px] font-mono px-1.5 py-0.5 rounded border ${PRIORITY_COLORS[task.priority] || ""}`}>{task.priority}</span>
        <span className="text-[9px] font-mono text-neutral-600 flex items-center gap-1 truncate min-w-0"><User className="w-2.5 h-2.5" /> {assigneesOf(task).map(nameOf).join(", ") || "—"}</span>
      </div>
      <div className="flex items-center gap-2 text-[9px] font-mono text-neutral-500">
        {task.due_date && <span className={overdue ? "text-rose-600 font-semibold flex items-center gap-0.5" : ""} data-testid={`kanban-due-${task.id}`}>{overdue && <AlertTriangle className="w-2.5 h-2.5" />}Due {task.due_date}</span>}
        <span className="ml-auto flex items-center gap-0.5"><MessageSquare className="w-2.5 h-2.5" />{task.comments?.length || 0}</span>
        <span className="flex items-center gap-0.5"><Paperclip className="w-2.5 h-2.5" />{task.attachments?.length || 0}</span>
      </div>
    </div>
  );
}
